import type { CSSProperties } from 'react';
import type { WatermarkSetup } from '@/types';
import { resolveText, type TokenContext } from '@/shared/utils/dataTokens';

export const DEFAULT_WATERMARK: WatermarkSetup = {
  enabled: true,
  mode: 'text',
  text: '',
  fontFamily: undefined,
  fontSize: 72,
  fontWeight: 700,
  color: '#000000',
  opacity: 0.08,
  rotation: -30,
  repeat: false,
  imageSrc: undefined,
  imageScale: 50,
};

/** How many copies to stamp when tiling. */
const TILE_COUNT_TEXT = 80;
const TILE_COUNT_IMAGE = 24;

interface WatermarkLayerProps {
  watermark?: WatermarkSetup;

  widthPx?: number;
  heightPx?: number;

  zoom?: number;

  fallbackFontFamily?: string;

  tokenCtx?: TokenContext;
}

export function WatermarkLayer({
  watermark,
  zoom = 1,
  fallbackFontFamily,
  tokenCtx,
}: WatermarkLayerProps) {
  if (!watermark?.enabled) return null;

  const overlay: CSSProperties = {
    position: 'absolute',
    inset: 0,
    overflow: 'hidden',
    pointerEvents: 'none',

    zIndex: 0,
  };

  // ── Image mode ─────────────────────────────────────────────────────────────
  // Opacity lives on the wrapper (not the <img>) so tiled copies don't compound.
  if ((watermark.mode ?? 'text') === 'image') {
    if (!watermark.imageSrc) return null;
    const scale = watermark.imageScale ?? 50;

    if (watermark.repeat) {
      return (
        <div style={overlay}>
          <div
            style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              width: '200%',
              height: '200%',
              transform: `translate(-50%, -50%) rotate(${watermark.rotation}deg)`,
              transformOrigin: 'center',
              display: 'flex',
              flexWrap: 'wrap',
              alignContent: 'center',
              justifyContent: 'center',
              gap: `${24 * zoom}px`,
              opacity: watermark.opacity,
            }}
          >
            {Array.from({ length: TILE_COUNT_IMAGE }, (_, i) => (
              <img
                key={i}
                src={watermark.imageSrc}
                alt=""
                style={{
                  // Tiles are a fraction of the single-stamp size so a full
                  // page's worth actually fits.
                  width: `${scale / 4}%`,
                  height: 'auto',
                  userSelect: 'none',
                }}
              />
            ))}
          </div>
        </div>
      );
    }

    return (
      <div
        style={{
          ...overlay,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          opacity: watermark.opacity,
        }}
      >
        <img
          src={watermark.imageSrc}
          alt=""
          style={{
            width: `${scale}%`,
            height: 'auto',
            transform: `rotate(${watermark.rotation}deg)`,
            userSelect: 'none',
          }}
        />
      </div>
    );
  }

  // ── Text mode ──────────────────────────────────────────────────────────────
  const text = tokenCtx
    ? resolveText(watermark.text, tokenCtx)
    : watermark.text;
  if (!text.trim()) return null;

  const fontFamily = watermark.fontFamily || fallbackFontFamily || 'inherit';
  const fontSizePx = `${watermark.fontSize * zoom}pt`;

  const textStyle: CSSProperties = {
    fontFamily,
    fontSize: fontSizePx,
    fontWeight: watermark.fontWeight,
    color: watermark.color,
    opacity: watermark.opacity,
    lineHeight: 1,
    whiteSpace: 'nowrap',
    userSelect: 'none',
  };

  if (watermark.repeat) {
    return (
      <div style={overlay}>
        <div
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            width: '200%',
            height: '200%',
            transform: `translate(-50%, -50%) rotate(${watermark.rotation}deg)`,
            transformOrigin: 'center',
            display: 'flex',
            flexWrap: 'wrap',
            alignContent: 'center',
            justifyContent: 'center',
            gap: `${watermark.fontSize * zoom * 0.6}px`,
            ...textStyle,
          }}
        >
          {Array.from({ length: TILE_COUNT_TEXT }, (_, i) => (
            <span key={i}>{text}</span>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        ...overlay,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <div
        style={{
          ...textStyle,
          transform: `rotate(${watermark.rotation}deg)`,
          textAlign: 'center',
        }}
      >
        {text}
      </div>
    </div>
  );
}
